import { debugLog, debugError } from '../utils/debugUtils';

class ApiService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    this._accessToken = null;
    this._refreshing = false;
    this._refreshQueue = [];
    // Injected by AuthService to avoid circular imports
    this._getRefreshToken = async () => null;
    this._saveRefreshToken = async () => {};
  }

  // Called once by AuthService after both singletons are created
  setTokenHandlers(getRefreshToken, saveRefreshToken) {
    this._getRefreshToken = getRefreshToken;
    this._saveRefreshToken = saveRefreshToken;
  }

  getAccessToken() {
    return this._accessToken;
  }

  setAccessToken(accessToken) {
    this._accessToken = accessToken || null;
  }

  clearTokens() {
    this._accessToken = null;
  }

  _buildHeaders(includeAuth = true, extra = {}) {
    const headers = { 'Content-Type': 'application/json', ...extra };
    if (includeAuth && this._accessToken) {
      headers['Authorization'] = `Bearer ${this._accessToken}`;
    }
    return headers;
  }

  async _refreshTokens() {
    const storedRefreshToken = await this._getRefreshToken();
    const body = storedRefreshToken ? JSON.stringify({ refresh_token: storedRefreshToken }) : null;
    const response = await fetch(`${this.baseUrl}/api/v1/auth/refresh-token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (!response.ok) {
      this.clearTokens();
      throw new Error('Refresh token expired');
    }

    const data = await response.json();
    this._accessToken = data.access_token;
    if (data.refresh_token) {
      await this._saveRefreshToken(data.refresh_token);
    }
    return data.access_token;
  }

  async request(method, path, body = null, includeAuth = true) {
    const url = `${this.baseUrl}${path}`;
    const options = {
      method,
      headers: this._buildHeaders(includeAuth),
      credentials: 'include',
    };
    if (body) options.body = JSON.stringify(body);

    debugLog(`[ApiService] ${method} ${url}`, body);

    let response = await fetch(url, options);

    // Token expired — attempt silent refresh and retry once
    if (response.status === 401 && includeAuth && !this._refreshing) {
      try {
        this._refreshing = true;
        await this._refreshTokens();
        this._refreshing = false;
        options.headers = this._buildHeaders(includeAuth);
        response = await fetch(url, options);
      } catch (err) {
        this._refreshing = false;
        debugError('[ApiService] Token refresh failed:', err);
        window.dispatchEvent(new CustomEvent('concordare:unauthorized'));
        throw err;
      }
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let errorCode = null;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        errorCode = errorData.code ?? null;
      } catch {
        // ignore parse error
      }
      debugError(`[ApiService] Error ${response.status} on ${method} ${path}:`, errorMessage);
      const error = new Error(errorMessage);
      if (errorCode !== null) error.code = errorCode;
      error.status = response.status;
      throw error;
    }

    // 204 No Content
    if (response.status === 204) return null;

    return response.json();
  }

  get(path, auth = true) {
    return this.request('GET', path, null, auth);
  }

  post(path, body, auth = true) {
    return this.request('POST', path, body, auth);
  }

  put(path, body, auth = true) {
    return this.request('PUT', path, body, auth);
  }

  delete(path, auth = true) {
    return this.request('DELETE', path, null, auth);
  }
}

export default new ApiService();
